import React, { useState } from 'react';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown } from 'lucide-react';

const NetIncomeCalculatorTool = () => {
  const [revenue, setRevenue] = useState('');
  const [expenses, setExpenses] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const calculate = () => {
    const revenueValue = parseFloat(revenue);
    const expensesValue = parseFloat(expenses);

    if (isNaN(revenueValue) || isNaN(expensesValue) || revenueValue < 0 || expensesValue < 0) {
      setResult(null);
      return;
    }

    const netProfit = revenueValue - expensesValue;
    const profitMargin = revenueValue > 0 ? (netProfit / revenueValue) * 100 : 0;
    const isProfit = netProfit >= 0;

    setResult({
      revenue: revenueValue,
      expenses: expensesValue,
      netProfit,
      profitMargin,
      isProfit
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setRevenue('');
    setExpenses('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    const absValue = Math.abs(value);
    const formatted = '$' + absValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return value < 0 ? '-' + formatted : formatted;
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Revenue ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={revenue}
              onChange={(e) => setRevenue(e.target.value)}
              placeholder="e.g., 100000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="revenue-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Expenses ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={expenses}
              onChange={(e) => setExpenses(e.target.value)}
              placeholder="e.g., 75000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="expenses-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-net-income-btn"
      >
        <Calculator size={18} />
        Calculate Net Profit
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="net-income-results">
          {/* Main Result */}
          <div className="p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {result.isProfit ? (
                  <TrendingUp size={18} className="text-green-400" />
                ) : (
                  <TrendingDown size={18} className="text-red-400" />
                )}
                <span className="text-sm text-gray-400">
                  {result.isProfit ? 'Net Profit' : 'Net Loss'}
                </span>
              </div>
              <button
                onClick={() => copyResult(result.netProfit, 'netProfit')}
                className={`p-1.5 rounded transition-colors ${
                  copied === 'netProfit' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="copy-net-profit"
              >
                <Copy size={14} />
              </button>
            </div>
            <div 
              className={`text-3xl font-bold ${result.isProfit ? 'text-green-400' : 'text-red-400'}`}
              data-testid="net-profit-result"
            >
              {formatCurrency(result.netProfit)}
            </div>
            {result.revenue > 0 && (
              <div className={`text-sm mt-1 ${result.isProfit ? 'text-green-400/70' : 'text-red-400/70'}`}>
                {result.profitMargin.toFixed(1)}% profit margin
              </div>
            )}
          </div>

          {/* Breakdown */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Total Revenue</span>
                <button
                  onClick={() => copyResult(result.revenue, 'revenue')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'revenue' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-xl font-bold text-blue-400">
                {formatCurrency(result.revenue)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Total Expenses</span>
                <button
                  onClick={() => copyResult(result.expenses, 'expenses')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'expenses' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-xl font-bold text-orange-400">
                {formatCurrency(result.expenses)}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Calculator size={16} className="text-blue-400" />
              <h4 className="text-sm font-medium text-gray-300">Calculation Summary</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Revenue</span>
                <span className="text-blue-400 font-medium">{formatCurrency(result.revenue)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Expenses</span>
                <span className="text-orange-400 font-medium">- {formatCurrency(result.expenses)}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Net Profit (Revenue - Expenses)</span>
                <span className={`font-medium ${result.isProfit ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(result.netProfit)}
                </span>
              </div>
              {result.revenue > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Profit Margin (Net ÷ Revenue × 100)</span>
                  <span className={`font-medium ${result.isProfit ? 'text-green-400' : 'text-red-400'}`}>
                    {result.profitMargin.toFixed(2)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Formula</h3>
        <div className="text-sm text-gray-400 font-mono bg-gray-900 p-3 rounded">
          Net Profit = Revenue - Expenses
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Note</h3>
        <div className="text-sm text-gray-400">
          This calculates simple net profit (also called net income). For a complete financial picture, consider additional factors like taxes, depreciation, interest, and other non-operating items.
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-net-income-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default NetIncomeCalculatorTool;
